import React, { useState } from 'react';
import {
  Pressable,
  Image,
  TextInput,
  View,
  TouchableWithoutFeedback,
  Keyboard,
  StyleSheet,
} from 'react-native';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import { isWeb as checkIsWeb } from '../util/platform';
import { InlineLoader } from '../components/LoadingOverlay';
import CustomText from '../components/CustomText';
import AppButton from '../components/AppButton';
import useLogin from '../hooks/useLogin';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { fonts } from '../style/fonts';
import { useTranslation } from '../i18n';

// Web-only: wrap children in a real <form> so browsers recognize login flows.
const WebFormWrapper = ({ onSubmit, children }) => {
  if (!checkIsWeb()) return <>{children}</>;
  return (
    // @ts-ignore
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        margin: 0,
        padding: 0,
      }}
      autoComplete="on"
    >
      <button type="submit" style={{ display: 'none' }} />
      {children}
    </form>
  );
};

const Login = (props) => {
  const { t } = useTranslation('auth');

  // Responsive styles hook
  const { isWeb, isDesktop, containerStyle } = useResponsiveStyles();

  // Focus states for V3 input styling
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Check if user just signed up (show "check email" message)
  const showCheckEmail = props.route?.params?.showCheckEmail || false;

  // Use the login hook for all auth logic
  const {
    email,
    setEmail,
    password,
    setPassword,
    hidePassword,
    togglePasswordVisibility,
    userFontSize,
    isBusy,
    biometricAvailable,
    hasStoredCreds,
    bioBusy,
    executeSignIn,
    tryBiometricLogin,
  } = useLogin(props.navigation, props.route.params);

  const icon = hidePassword ? 'eye-off' : 'eye';

  // Responsive widths
  const inputWidth = isDesktop ? wp(40) : wp(85);
  const linkFontSize = isDesktop ? 18 : 16;
  const logoWidth = isDesktop ? 320 : 240;
  const logoHeight = isDesktop ? 89 : 67;

  // Card width for form container
  const cardWidth = isDesktop ? wp(45) : wp(90);

  return (
    <TouchableWithoutFeedback
      onPress={!isWeb ? Keyboard.dismiss : () => {}}
      accessible={false}
    >
      <View
        style={[
          containerStyle,
          styles.container,
        ]}
      >
        {/* Gold accent bar at top */}
        <View style={styles.accentBar} />

        <WebFormWrapper onSubmit={executeSignIn}>
          {/* Logo */}
          <Image
            source={require('../assets/images/logo-black.png')}
            style={[styles.logo, { width: logoWidth, height: logoHeight }]}
          />

          {/* Tagline */}
          <CustomText
            style={styles.tagline}
            font="regular"
            size={16}
            color={AppStyles.colorSet.textSecondary}
          >
            {t('login.tagline')}
          </CustomText>

          {/* Check email message after signup */}
          {showCheckEmail && (
            <View style={[styles.checkEmailBanner, { width: cardWidth }]}>
              <Feather name="mail" size={18} color="#1D4ED8" style={{ marginRight: 8 }} />
              <CustomText font="medium" size={14} color="#1D4ED8">
                {t('login.checkEmail')}
              </CustomText>
            </View>
          )}

          {/* Form Card */}
          <View style={[styles.formCard, { width: cardWidth }]}>
            {/* Email input - V3 style */}
            <View
              style={[
                styles.inputContainer,
                emailFocused && styles.inputContainerFocused,
              ]}
            >
              <Feather
                name="mail"
                size={20}
                color={emailFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                style={styles.inputIcon}
              />
              <TextInput
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="username"
                allowFontScaling={true}
                value={email}
                onChangeText={(text) => {
                  setEmail(text.trim());
                }}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                placeholder={t('login.emailPlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                style={[styles.inputText, { fontSize: userFontSize }]}
                {...(isWeb
                  ? { autoComplete: 'username', id: 'username', name: 'username', inputMode: 'email' }
                  : {})}
              />
            </View>

            {/* Password input - V3 style */}
            <View
              style={[
                styles.inputContainer,
                passwordFocused && styles.inputContainerFocused,
                { marginTop: hp(2) },
              ]}
            >
              <Feather
                name="lock"
                size={20}
                color={passwordFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                style={styles.inputIcon}
              />
              <TextInput
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="password"
                secureTextEntry={hidePassword}
                value={password}
                onChangeText={(text) => {
                  setPassword(text);
                }}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder={t('login.passwordPlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                style={[styles.inputText, { fontSize: userFontSize }]}
                {...(isWeb
                  ? { autoComplete: 'current-password', id: 'password', name: 'password' }
                  : {})}
              />
              <Pressable onPress={togglePasswordVisibility} style={styles.eyeButton}>
                <Feather
                  name={icon}
                  size={20}
                  color={AppStyles.colorSet.textSecondary}
                />
              </Pressable>
            </View>

            {/* Sign In button - V3 style */}
            <View style={{ marginTop: hp(3) }}>
              <AppButton
                title={t('login.signIn')}
                onPress={executeSignIn}
                isEnabled={email.length > 0}
                isBusy={isBusy}
                fullWidth
              />
            </View>

            {/* Biometric login - text link style */}
            {!isWeb && biometricAvailable && hasStoredCreds && (
              <Pressable
                onPress={tryBiometricLogin}
                disabled={bioBusy}
                style={styles.biometricLink}
              >
                <CustomText
                  font="regular"
                  size={linkFontSize}
                  color={AppStyles.colorSet.textSecondary}
                >
                  {t('login.orUse')}
                </CustomText>
                <CustomText
                  font="medium"
                  size={linkFontSize}
                  color={AppStyles.colorSet.brandPrimary}
                >
                  {bioBusy ? t('login.authenticating') : 'Face ID'}
                </CustomText>
                {bioBusy && <InlineLoader style={{ marginLeft: 8 }} />}
              </Pressable>
            )}
          </View>
        </WebFormWrapper>

        {/* Footer links */}
        <View style={styles.linksContainer}>
          {/* Forgot password */}
          <Pressable
            onPress={() => {
              props.navigation.navigate('ForgotPassword');
            }}
            style={[styles.link, { cursor: 'pointer' }]}
          >
            {({ hovered, focused }) => (
              <CustomText
                font="regular"
                size={linkFontSize}
                color={AppStyles.colorSet.textSecondary}
                style={(hovered || focused) && { textDecorationLine: 'underline' }}
              >
                {t('login.forgotPassword')}
              </CustomText>
            )}
          </Pressable>

          {/* Sign up */}
          <Pressable
            onPress={() => {
              props.navigation.navigate('SignUp');
            }}
            style={[styles.link, { cursor: 'pointer' }]}
          >
            {({ hovered, focused }) => (
              <>
                <CustomText font="regular" size={linkFontSize} color={AppStyles.colorSet.textSecondary}>
                  {t('login.newUser')}
                </CustomText>
                <CustomText
                  font="semiBold"
                  size={linkFontSize}
                  color={AppStyles.colorSet.brandPrimary}
                  style={(hovered || focused) && { textDecorationLine: 'underline' }}
                >
                  {t('login.createAccount')}
                </CustomText>
              </>
            )}
          </Pressable>

          {/* Privacy Policy */}
          <Pressable
            onPress={() => {
              props.navigation.navigate('PrivacyPolicy');
            }}
            style={[styles.link, { marginTop: hp(4), cursor: 'pointer' }]}
          >
            {({ hovered, focused }) => (
              <CustomText
                font="regular"
                size={14}
                color={AppStyles.colorSet.textMuted}
                style={(hovered || focused) && { textDecorationLine: 'underline' }}
              >
                {t('login.privacyPolicy')}
              </CustomText>
            )}
          </Pressable>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: wp(5),
    paddingTop: hp(2),
    paddingBottom: hp(4),
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  logo: {
    resizeMode: 'contain',
    marginTop: hp(6),
  },
  tagline: {
    marginTop: hp(1),
    marginBottom: hp(4),
  },
  checkEmailBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginBottom: hp(2),
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputContainerFocused: {
    backgroundColor: AppStyles.colorSet.inputBackgroundFocused,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.inputBorderFocused,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  inputIcon: {
    marginRight: 12,
  },
  inputText: {
    flex: 1,
    fontFamily: fonts.POPPINS.regular,
    color: AppStyles.colorSet.textPrimary,
  },
  eyeButton: {
    // WCAG minimum touch target: 44x44px
    padding: 12,
    minHeight: 44,
    minWidth: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  biometricLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: hp(2),
    cursor: 'pointer',
  },
  linksContainer: {
    alignItems: 'center',
    marginTop: hp(4),
  },
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: hp(2),
    cursor: 'pointer',
  },
});

export default Login;
